/**
 * ActivityBar — Unified chat-centric surface with contextual actions.
 *
 * Layout zones (Phase 7 redesign):
 *   Header:      Page title left, status indicators right (merged StatusBand)
 *   Buttons:     Hero (centered, stacked) or compact (inline row) action buttons
 *   Chat:        Pure chat thread (user/assistant only — no system messages)
 *   Input:       AI preset chips + text input + doc type dropdown
 *
 * Header icons (Mic, Upload, Export) open the bar and trigger action flows.
 * No tabs — consumers use `openActivityBar(action)` / `closeActivityBar()`.
 *
 * See: docs/features/infrastructure/app-shell.md
 */
import React, { useMemo, useCallback, useState, useEffect, useRef } from "react";
import { X, Mic, Check, ChevronDown, ChevronUp } from "lucide-react";
import Tooltip from "@mui/material/Tooltip";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { useLocation } from "react-router-dom";
import { useAppShellContext } from "../../contexts/AppShellContext";
import { useActionChips } from "../../hooks/useActionChips";
import { useActivityMessages } from "../../hooks/useActivityMessages";
import { useChatMessages } from "../../hooks/useChatMessages";
import { useChatCollapse } from "../../hooks/useChatCollapse";
import { usePatientNameIndex } from "../../hooks/usePatientNameIndex";
import { useFeatureFlags } from "../../contexts/FeatureFlagContext";
import { buildActionHandlers } from "../../utils/actionHandlers";
import { MIN_DOCUMENTS_FOR_EXPORT } from "../../globals";
import { prepareExportData, handleExportProcessing } from "../../utils/exportProcessing";
import {
    buildSummarySourceText,
    formatSummaryPrerequisiteMessage,
    resolveSummaryPatientDocuments,
} from "../../utils/summaryGeneration";
import { submitForProcessing } from "../../utils/processingService";
import { addData } from "../../utils/firestoreService";
import { createInitialDocumentStructure } from "../../utils/documentCreationUtils";
import { getSharedDocumentMetadata } from "../../utils/sharingService";
import { useActiveJobsContext } from "../../contexts/ActiveJobsContext";
import { showNotification } from "../../utils/notificationSystem";
import QualityGateDialog from "./QualityGateDialog";
import CancelRecordingConfirmModal from "../recording/CancelRecordingConfirmModal";
// usePrepContent removed — StatusBand merged into header (Phase 7)
import { useUploadFlow } from "../../hooks/useUploadFlow";
import { useFlowChips } from "../../hooks/useFlowChips";
import { useAuth } from "../../utils/authContext";
import { menuPaperSx, menuItemSx } from "../../utils/muiOverrides";
import { SESSION_STATE } from "../../utils/sessionTimeout";
import ChatMessages from "./ChatMessages";
import TriggerChips from "./TriggerChips";
import InputRow from "./InputRow";
import ActivityBarHeader from "./ActivityBarHeader";
import { useHeaderTakeover } from "../../hooks/useHeaderTakeover";
import { detectContext } from "../../utils/activityBarContext";
import { getData } from "../../utils/firestoreService";
import { isProfileComplete } from "../../utils/userProfile";
import { ManageDocumentTypesModal, MANAGE_OPTION_VALUE } from "../../utils/documentTypeSelection";

const ActivityBar = ({
    // Recording props (forwarded to StatusBand)
    patientId,
    isStreaming,
    setIsStreaming,
    isProcessing,
    setIsProcessing,
    userId,
    navigate,
    session,
    processing,
    // Upload / shared props
    dropdownState,
    networkStatus,
    // Upload doc tracking (for processing listener)
    onUploadDocCreated,
    uploadDocId,
    // Document state
    hasUnsavedChanges = false,
    // User settings for usage tracking
    actionUsage = {},
    // Shared
    isViewOnly = false,
    // Inline mode (Clinical Day View dashboard)
    inline = false,
    patientName = "",
    lockedContext = null,
    // Phase 12: Track active jobs for submission gap
    hasActiveJobs = false,
}) => {
    // ── Auth (session timeout) ──
    const { sessionState, extendSessionOnAction } = useAuth();

    // ── Export flow state ──
    const [showExportConfirmation, setShowExportConfirmation] = useState(false);
    const [selectedExportType, setSelectedExportType] = useState("");
    const [exportButtonText, setExportButtonText] = useState("Begin Export");
    const [currentExportDocId, setCurrentExportDocId] = useState(null);

    // ── Summary flow state (Phase 1 — see summary-generation.md) ──
    const [showSummaryConfirmation, setShowSummaryConfirmation] = useState(false);
    const [selectedSummaryTypeId, setSelectedSummaryTypeId] = useState("");
    const [summaryButtonText, setSummaryButtonText] = useState("Generate");
    const { addPendingJob: addSummaryPendingJob, removePendingJob: removeSummaryPendingJob } = useActiveJobsContext();

    // Upload status lives here (shared with useActivityMessages + useUploadFlow)
    const [uploadStatus, setUploadStatus] = useState("idle"); // idle | extracting | processing | complete | error

    const inputRowRef = useRef(null);

    const {
        activityBarPosition,
        setActivityBarPosition,
        activityBarOpen,
        pendingAction,
        clearPendingAction,
        openActivityBar,
        closeActivityBar,
        closeIfImplicit,
        selectedPatientId: ctxSelectedPatientId,
    } = useAppShellContext();

    const location = useLocation();

    // ── Context detection ──
    const currentContext = detectContext(location.pathname, ctxSelectedPatientId);

    // ── Phase 6: Header takeover ──
    const { isTakeover, takeoverContext } = useHeaderTakeover({
        currentContext,
        activityBarOpen,
        inline,
        lockedContext,
    });

    // ── Profile completeness (for dashboard "Update Account" swap) ──
    // v3/AppShell only — ActivityBar only renders inside AppShell.
    const [profileIncomplete, setProfileIncomplete] = useState(false);
    useEffect(() => {
        let cancelled = false;
        if (!userId) return undefined;
        (async () => {
            try {
                const userDoc = await getData(`users/${userId}`);
                if (!cancelled) setProfileIncomplete(!isProfileComplete(userDoc));
            } catch (err) {
                // Non-fatal — just leave the chip as View All.
                if (!cancelled) setProfileIncomplete(false);
            }
        })();
        return () => { cancelled = true; };
    }, [userId]);

    // ── Resolve trigger chips from actions.json ──
    const { chips, chatPresets } = useActionChips({
        currentContext,
        stateFlags: {
            isRecording: isStreaming,
            hasUnsavedChanges,
            profileIncomplete,
        },
        actionUsage,
    });

    // ── Activity message timeline → transient header status ──
    const { statusMessage, clearStatus } = useActivityMessages({
        session,
        isStreaming,
    });

    // ── Upload flow ──
    const {
        showUploadUI, setShowUploadUI,
        uploadMode, setUploadMode,
        selectedFile, setSelectedFile,
        uploadTextContent, setUploadTextContent,
        fileInputRef,
        handleCancelUpload, handleBeginUpload, handleFileSelect,
    } = useUploadFlow({
        patientId, userId, dropdownState, session,
        setIsProcessing, onUploadDocCreated, addMessage: () => {},
        sessionState, extendSessionOnAction, setUploadStatus,
        onUploadComplete: closeIfImplicit,
    });

    // ── AI Chat ──
    const { flags = {} } = useFeatureFlags();
    // Document ID: route state (editor), active recording, or active upload
    const routeDocId = location.state?.docId || null;
    const activeDocId = session?.documentId || uploadDocId || routeDocId;

    const {
        chatMessages, sendMessage, sendPreset, isLoading: chatLoading,
        error: chatError, retry: chatRetry, clearChat,
    } = useChatMessages({
        userId,
        patientId,
        documentId: activeDocId,
        documentData: {
            typeTitle: dropdownState?.selectedDocumentType || null,
        },
        pageContext: currentContext,
        patientName,
        enabled: !!flags.aiAssistant,
    });

    // ── Chat messages (no system messages — those go to header status) ──
    const messages = chatMessages;

    // ── Patient quick search — name index for the type-ahead dropdown ──
    // Loads once per session when the bar is actually usable (AI on + open/inline).
    const { patients: quickSearchPatients } = usePatientNameIndex({
        userId,
        enabled: !!flags.aiAssistant && (activityBarOpen || inline),
    });

    // ── Build action handler map ──
    const handlers = useMemo(
        () =>
            buildActionHandlers({
                session,
                navigate,
                patientId,
                userId,
                dropdownState,
                openActivityBar,
                setIsProcessing,
            }),
        [session, navigate, patientId, userId, dropdownState, openActivityBar, setIsProcessing]
    );

    // Chip click — dispatch through handler registry
    const handleChipClick = useCallback(
        (chip) => {
            if (chip.handler && handlers[chip.handler]) {
                handlers[chip.handler]();
            } else {
                console.warn("[ActivityBar] No handler for chip:", chip.id, chip.handler);
            }
        },
        [handlers]
    );

    // ── Export flow helpers (hooks must be above early return) ──
    const exportDocumentTypes = dropdownState?.documentTypes?.filter((type) => type.category === 3) || [];
    const exportData = useMemo(
        () => prepareExportData(dropdownState?.patientDocuments || [], MIN_DOCUMENTS_FOR_EXPORT),
        [dropdownState?.patientDocuments]
    );
    const hasExportData = exportData.totalDocuments > 0;
    const meetsMinimumRequirement = exportData.totalDocuments >= MIN_DOCUMENTS_FOR_EXPORT;

    const handleStartExport = useCallback(() => {
        if (!patientId) return;
        setShowExportConfirmation(true);
        setCurrentExportDocId(null);
        setExportButtonText("Begin Export");
        // Auto-select first export type if not already selected
        if (!selectedExportType && exportDocumentTypes.length > 0) {
            setSelectedExportType(exportDocumentTypes[0].name);
        }
    }, [patientId, selectedExportType, exportDocumentTypes]);

    const handleCancelExport = useCallback(() => {
        setShowExportConfirmation(false);
        setSelectedExportType("");
        setExportButtonText("Begin Export");
    }, []);

    const handleConfirmExport = useCallback(async () => {
        if (!patientId || !selectedExportType) return;

        // Block exports during grace period
        if (sessionState === SESSION_STATE.GRACE || sessionState === SESSION_STATE.GRACE_URGENT || sessionState === SESSION_STATE.EXPIRED) {
            showNotification("Session expired — please save your work and log in again.", "warning", 6000);
            return;
        }
        extendSessionOnAction?.();

        const selectedDocType = exportDocumentTypes.find((t) => t.name === selectedExportType);
        if (!selectedDocType) return;

        const started = await handleExportProcessing({
            selectedDocumentType: selectedExportType,
            selectedDocumentTypeId: selectedDocType.id,
            dropdownState,
            patientId,
            navigate,
            setButtonText: setExportButtonText,
            setCurrentUploadDocId: setCurrentExportDocId,
            setIsProcessing,
            userSettings: dropdownState?.userSettings,
            setIsFunctionHeaderOpen: () => {},
            documentData: exportData,
            setCurrentExportDocId,
            shareInfo: session?.shareInfo || null,
            userId,
            addPendingJob: addSummaryPendingJob,
            removePendingJob: removeSummaryPendingJob,
        });

        if (started) {
            setShowExportConfirmation(false);
            closeIfImplicit();
        }
    }, [patientId, selectedExportType, exportDocumentTypes, dropdownState, navigate, setIsProcessing, exportData, session?.shareInfo, sessionState, extendSessionOnAction, closeIfImplicit, addSummaryPendingJob, removeSummaryPendingJob, userId]);

    // ── Summary flow helpers ──
    const summaryDocumentTypes = useMemo(
        () => (dropdownState?.documentTypes || []).filter((t) => t.format === "summary"),
        [dropdownState?.documentTypes]
    );

    const handleStartSummary = useCallback(() => {
        if (!patientId) return;
        setShowSummaryConfirmation(true);
        setSummaryButtonText("Generate");
        if (!selectedSummaryTypeId && summaryDocumentTypes.length > 0) {
            setSelectedSummaryTypeId(String(summaryDocumentTypes[0].id));
        }
    }, [patientId, selectedSummaryTypeId, summaryDocumentTypes]);

    // Back-fill the default summary type if the picker was opened before the
    // document types finished loading. handleStartSummary only sets the default
    // once, at open time; when the flow is triggered on a cold session the type
    // list is still empty then, so without this the Generate chip stays stuck on
    // "Select a summary type" (value="" displays the first <option> but never
    // commits) even after the types arrive. Re-applies the default reactively.
    useEffect(() => {
        if (showSummaryConfirmation && !selectedSummaryTypeId && summaryDocumentTypes.length > 0) {
            setSelectedSummaryTypeId(String(summaryDocumentTypes[0].id));
        }
    }, [showSummaryConfirmation, selectedSummaryTypeId, summaryDocumentTypes]);

    const handleCancelSummary = useCallback(() => {
        setShowSummaryConfirmation(false);
        setSummaryButtonText("Generate");
    }, []);

    const handleConfirmSummary = useCallback(async () => {
        if (!patientId) {
            showNotification(
                "Select a patient before generating a summary.",
                "warning",
                6000,
                { priority: true }
            );
            return;
        }
        if (!selectedSummaryTypeId) {
            showNotification(
                "Select a summary type before generating.",
                "warning",
                6000,
                { priority: true }
            );
            return;
        }

        if (sessionState === SESSION_STATE.GRACE || sessionState === SESSION_STATE.GRACE_URGENT || sessionState === SESSION_STATE.EXPIRED) {
            showNotification("Session expired — please save your work and log in again.", "warning", 6000);
            return;
        }
        extendSessionOnAction?.();

        const docType = summaryDocumentTypes.find((t) => String(t.id) === String(selectedSummaryTypeId));
        if (!docType) {
            showNotification(
                "The selected summary type is no longer available. Refresh and choose another type.",
                "warning",
                8000,
                { priority: true }
            );
            return;
        }

        const allDocumentTypes = dropdownState?.documentTypes || [];
        let patientDocuments;
        try {
            patientDocuments = await resolveSummaryPatientDocuments({
                patientDocuments: dropdownState?.patientDocuments || [],
                refreshPatientData: dropdownState?.refreshPatientData,
            });
        } catch (error) {
            console.error("[ActivityBar] Failed to refresh summary source documents:", error);
            showNotification(
                "Unable to refresh this patient's completed notes. Please try again.",
                "error",
                8000,
                { priority: true }
            );
            return;
        }
        const patientData = dropdownState?.patientData || {};

        const {
            combinedText,
            sourceDocs,
            dateRange,
            unmetPrerequisites,
            configurationError,
        } = buildSummarySourceText({
            docType,
            patientDocuments,
            allDocumentTypes,
            actingUserId: userId,
        });

        if (configurationError === "missing_prerequisites") {
            showNotification(
                `${docType.name || "This summary"} is not configured with source-note requirements. Contact an administrator.`,
                "error",
                10000
            );
            return;
        }

        if (unmetPrerequisites.length > 0) {
            showNotification(
                formatSummaryPrerequisiteMessage(docType.name, unmetPrerequisites),
                "warning",
                10000,
                { key: `summary-prereqs-${docType.id}`, priority: true }
            );
            return;
        }

        if (!combinedText || sourceDocs.length === 0) {
            showNotification(
                "The required notes do not contain enough completed clinical content to generate this summary.",
                "warning",
                8000,
                { priority: true }
            );
            return;
        }

        setSummaryButtonText("Generating…");
        setIsProcessing(true);

        // 1. Create the destination Firestore document.
        const initialData = {
            ...createInitialDocumentStructure(
                "summary",
                docType.name,
                docType.id,
                docType.category,
                Date.now(),
                docType.sections || null
            ),
            ...getSharedDocumentMetadata(userId, session?.shareInfo),
            isExport: true,
            sourceDocumentIds: sourceDocs.map((d) => d.id || d.documentId).filter(Boolean),
            sourceDocumentCount: sourceDocs.length,
            dateRange,
        };

        let newDocId;
        try {
            newDocId = await addData(`patients/${patientId}/documents`, initialData);
            const patientName = `${patientData?.firstName || ""} ${patientData?.lastName || ""}`.trim();
            addSummaryPendingJob(newDocId, docType.name || "Summary", patientId, patientName);
        } catch (err) {
            console.error("[ActivityBar] Failed to create summary document:", err);
            showNotification("Failed to create summary document", "error");
            setIsProcessing(false);
            setSummaryButtonText("Generate");
            return;
        }

        // 2. Submit for v3 processing — sources already inlined in combinedText.
        // Prefer the real Firestore doc-type id (the v2 transform exposes it as
        // `firestoreId`; under v3 the `id` already IS the Firestore id).
        const submittedDocTypeId = docType.firestoreId || docType.id;
        const result = await submitForProcessing({
            text: combinedText,
            documentTypeId: submittedDocTypeId,
            patientData,
            patientDocuments: [], // no patientHistory — sources inlined above
            documentId: newDocId,
            patientId,
            source: "summary",
        });

        if (!result.success) {
            if (result.status !== "prerequisites_not_met") {
                showNotification(result.error || "Summary generation failed", "error");
            }
            removeSummaryPendingJob(newDocId);
            setIsProcessing(false);
            setSummaryButtonText("Generate");
            return;
        }

        showNotification(
            `${docType.name || "Summary"} generation started. Select this message to open it.`,
            "success",
            10000,
            {
                key: `summary-started-${newDocId}`,
                priority: true,
                onClick: () => navigate("/document", {
                    state: { patientId, docId: newDocId, from: location.pathname || "/patient" },
                }),
            }
        );

        setShowSummaryConfirmation(false);
        setSummaryButtonText("Generate");
        closeIfImplicit();
    }, [
        patientId, selectedSummaryTypeId, summaryDocumentTypes, dropdownState,
        userId, session?.shareInfo, setIsProcessing, sessionState, extendSessionOnAction,
        closeIfImplicit, addSummaryPendingJob, removeSummaryPendingJob, navigate, location.pathname,
    ]);

    // Auto-open recording confirmation when triggered via header icon or trigger chip
    useEffect(() => {
        if (pendingAction === "record" && !isStreaming) {
            if (!patientId) {
                clearPendingAction();
                clearStatus();
                // No patient context — navigate to patient page which shows the select modal
                navigate("/patient", { state: { pendingAction: "record" } });
                return;
            }
            // Wait for session data (document types) to load before showing confirmation
            if (!session?.isReady) return;
            // Reset upload flow if active
            if (showUploadUI) {
                handleCancelUpload();
            }
            // Reset summary picker if active
            if (showSummaryConfirmation) {
                handleCancelSummary();
            }
            clearPendingAction();
            clearStatus();
            session?.requestStart?.();
        }
    }, [pendingAction, session?.isReady]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-open export confirmation when triggered via header icon or trigger chip
    useEffect(() => {
        if (pendingAction === "export" && !isStreaming) {
            // Reset other active flows
            if (session?.showConfirmation) {
                session.cancelConfirmation();
            }
            if (showUploadUI) {
                handleCancelUpload();
            }
            if (showSummaryConfirmation) {
                handleCancelSummary();
            }
            clearPendingAction();
            clearStatus();
            if (!patientId) {
                navigate("/patient", { state: { pendingAction: "export" } });
                return;
            }
            handleStartExport();
        }
    }, [pendingAction]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-open summary picker when triggered via header icon
    useEffect(() => {
        if (pendingAction === "summary" && !isStreaming) {
            if (!patientId) {
                clearPendingAction();
                clearStatus();
                navigate("/patient", { state: { pendingAction: "summary" } });
                return;
            }
            // Wait for session data (document types) to load before opening the
            // picker — otherwise handleStartSummary runs with an empty type list
            // and can't set a default selection, leaving Generate stuck disabled.
            // Mirrors the upload auto-open guard below. Re-runs once isReady flips.
            if (!session?.isReady) return;
            if (session?.showConfirmation) session.cancelConfirmation();
            if (showUploadUI) handleCancelUpload();
            clearPendingAction();
            clearStatus();
            handleStartSummary();
        }
    }, [pendingAction, session?.isReady]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-open upload UI when triggered via header icon, trigger chip, or "Upload Instead"
    useEffect(() => {
        if (pendingAction === "upload" && !isStreaming) {
            if (!patientId) {
                clearPendingAction();
                clearStatus();
                navigate("/patient", { state: { pendingAction: "upload" } });
                return;
            }
            // Wait for session data (document types) to load before showing upload UI
            if (!session?.isReady) return;
            // Reset recording confirmation if active (cleans up prepared mic)
            if (session?.showConfirmation) {
                session.cancelConfirmation();
            }
            // Reset summary picker if active
            if (showSummaryConfirmation) {
                handleCancelSummary();
            }
            clearPendingAction();
            clearStatus();
            // Reset upload state so a fresh upload flow starts cleanly
            // (previous upload may have left uploadStatus at "processing")
            setUploadStatus("idle");
            setSelectedFile(null);
            setUploadTextContent("");
            setShowUploadUI(true);
        }
    }, [pendingAction, session?.isReady]); // eslint-disable-line react-hooks/exhaustive-deps

    // Phase 12 cleanup: Escape-to-close removed — Activity Bar closes only via toggle button.
    // See docs/features/infrastructure/app-shell.md

    // Focus InputRow text field when Activity Bar opens
    useEffect(() => {
        if (activityBarOpen && !inline) {
            // Small delay to allow render to complete
            const timer = setTimeout(() => inputRowRef.current?.focus(), 100);
            return () => clearTimeout(timer);
        }
    }, [activityBarOpen, inline]);

    // Recording confirmation state
    const showRecordingConfirmation = session?.showConfirmation && !isStreaming;

    // Show export confirmation when pendingAction is 'export' and on patient page
    const showExportUI = showExportConfirmation && currentContext === "patientDetail" && !isStreaming;

    // Show summary picker when triggered
    const showSummaryUI = showSummaryConfirmation && !isStreaming;

    // Show upload UI when triggered and not busy
    const showUploadActive = showUploadUI && !isStreaming;

    // Processing-complete state — handled by ProcessingPill toast/bell notifications now
    // (View Document flow chip removed — Phase 12)

    // ── Mic menu state (Select Mic chip opens a popover) ──
    const [micMenuAnchor, setMicMenuAnchor] = useState(null);

    // ── Compose flowChips for TriggerChips (buttons for active flows) ──
    const flowChips = useFlowChips({
        isStreaming, showRecordingConfirmation, showExportUI, showUploadActive, showSummaryUI, showContinue: false,
        session, processing, selectedExportType, hasExportData, exportButtonText,
        handleConfirmExport, handleCancelExport, handleBeginUpload, handleCancelUpload,
        selectedSummaryTypeId, summaryButtonText, summaryDocumentTypes,
        handleConfirmSummary, handleCancelSummary,
        uploadMode, selectedFile, uploadTextContent, uploadStatus, uploadDocId,
        navigate, patientId, closeActivityBar, openActivityBar,
        setMicMenuAnchor, setUploadMode, setSelectedFile, setUploadTextContent,
    });

    // ── Document type dropdown for recording/upload prep flows ──
    const [isManageDocsOpen, setIsManageDocsOpen] = useState(false);
    const docTypeDropdown = useMemo(() => {
        if (isStreaming || showRecordingConfirmation || (showUploadActive && uploadStatus === "idle")) {
            const options = Array.isArray(session?.documentTypes)
                ? session.documentTypes.filter((t) => t.category !== 3)
                : [];
            const hiddenCount = Array.isArray(dropdownState?.userSettings?.hiddenDocumentTypeIds)
                ? dropdownState.userSettings.hiddenDocumentTypeIds.length
                : 0;
            return {
                value: options.length > 0 ? session.selectedDocumentType : "__no_visible_document_types__",
                onChange: (e) => {
                    if (e.target.value === MANAGE_OPTION_VALUE) {
                        setIsManageDocsOpen(true);
                        return;
                    }
                    session.selectDocumentType?.(e);
                },
                options,
                manageLabel: hiddenCount > 0
                    ? `MORE (${hiddenCount}): Manage Documents…`
                    : "Manage Documents…",
            };
        }
        return null;
    }, [isStreaming, showRecordingConfirmation, showUploadActive, uploadStatus, session?.documentTypes, session?.selectedDocumentType, session?.selectDocumentType, dropdownState?.userSettings?.hiddenDocumentTypeIds]);

    // Saved messages loading must not override the user's collapsed preference.
    const [chatCollapsed, setChatCollapsed] = useChatCollapse(userId);
    const hasChat = chatMessages.length > 0;

    // ── Early return if bar is closed (inline mode is always open) ──
    if (!inline && !activityBarOpen) return null;

    const isHorizontal = true; // Only top/bottom positions remain

    // Cycle: top ↔ bottom
    const handlePositionCycle = () => {
        setActivityBarPosition(activityBarPosition === "top" ? "bottom" : "top");
    };

    // Chat send — AI Chat
    const handleSend = (message) => {
        if (flags.aiAssistant) {
            setChatCollapsed(false);
            sendMessage(message);
        }
    };

    const handleChatPreset = (...args) => {
        if (flags.aiAssistant) {
            setChatCollapsed(false);
            sendPreset(...args);
        }
    };

    const handleFileDrop = (files) => {
        const file = files?.[0];
        if (file) {
            handleFileSelect(file);
        }
        handlers.startUpload?.();
    };

    // Recording-only mode: on non-activity pages (generic context), show only
    // the recording header (meter, timer, controls) — hide chat, input, upload.
    const recordingOnly = isStreaming && currentContext === "generic";

    // Determine Activity Bar classes
    const chatActiveClass = hasChat && !chatCollapsed ? " activity-bar-chat-active" : "";
    const positionClass = inline ? "activity-bar-inline" : `activity-bar-${activityBarPosition}`;
    const isDashboardContext = currentContext === "dashboard" || currentContext === "dashboardPatientSelected";

    // Close: only show inline close for bottom position (top uses header toggle)
    const showInlineClose = !inline && activityBarPosition === "bottom";
    const closeDisabled = isStreaming;

    const barContent = (
        <div
            className={`activity-bar ${positionClass}${chatActiveClass}${
                isStreaming ? " activity-bar-streaming" : ""
            }${
                isDashboardContext ? " activity-bar-dashboard" : ""}`}
        >
            {/* Control cluster — close button (hidden in inline mode) */}
            <div className="activity-bar-controls">
                {!inline && showInlineClose && (
                        <Tooltip
                            title={closeDisabled ? "Cannot close while active" : "Close"}
                            arrow
                            placement="bottom"
                        >
                            <button
                                className={`activity-bar-control-btn activity-bar-control-btn-close${closeDisabled ? " activity-bar-control-btn-disabled" : ""}`}
                                onClick={() => closeActivityBar(currentContext)}
                                aria-label="Close activity bar"
                                disabled={closeDisabled}
                            >
                                <X size={14} />
                            </button>
                        </Tooltip>
                    )}
                </div>

            {/* Header: page title left, status indicators right (merged StatusBand) */}
            <ActivityBarHeader
                takeoverContext={takeoverContext}
                patientName={patientName}
                isDashboard={currentContext === "dashboard" || currentContext === "dashboardPatientSelected"}
                isViewOnly={isViewOnly}
                lockedContext={lockedContext}
                isRecording={isStreaming || showRecordingConfirmation}
                isActuallyStreaming={isStreaming}
                isPaused={session?.isPaused || false}
                elapsedSeconds={session?.recordingTime || 0}
                volumeLevel={session?.isPaused ? 0 : (session?.volumeLevel || 0)}
                statusMessage={statusMessage}
                warningMessage={session?.audioWarning || ""}
                chunkStatusHistory={session?.chunkStatusHistory || []}
                localSilenceDetected={session?.localSilenceDetected || false}
                recordingStatus={session?.recordingStatus || null}
            />

            {/* Summary picker — choose a summary doc type to generate */}
            {!recordingOnly && showSummaryUI && (
                <div className="upload-panel-body">
                    {summaryDocumentTypes.length === 0 ? (
                        <p>No summary types available for this company.</p>
                    ) : (
                        <>
                            <label htmlFor="summary-type-select" style={{ fontSize: 13, marginBottom: 6, display: "block" }}>
                                Summary type
                            </label>
                            <select
                                id="summary-type-select"
                                className="upload-panel-textarea"
                                style={{ height: 40, padding: "0 10px" }}
                                value={selectedSummaryTypeId}
                                onChange={(e) => setSelectedSummaryTypeId(e.target.value)}
                            >
                                {summaryDocumentTypes.map((t) => (
                                    <option key={t.id} value={t.id}>{t.name}</option>
                                ))}
                            </select>
                            <p style={{ fontSize: 12, marginTop: 8, opacity: 0.7 }}>
                                Generates a new document by synthesizing this patient&apos;s prior records.
                            </p>
                        </>
                    )}
                </div>
            )}

            {/* Upload input zone — inline file drop or text paste */}
            {!recordingOnly && showUploadActive && uploadStatus === "idle" && (
                <div className="upload-panel-body">
                    {uploadMode === "file" ? (
                        <div
                            className={`upload-panel-drop-zone${selectedFile ? " dropped" : ""}`}
                            onClick={() => fileInputRef.current?.click()}
                            onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("dragging"); }}
                            onDragLeave={(e) => e.currentTarget.classList.remove("dragging")}
                            onDrop={(e) => {
                                e.preventDefault();
                                e.currentTarget.classList.remove("dragging");
                                const file = e.dataTransfer.files?.[0];
                                if (file) handleFileSelect(file);
                            }}
                        >
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".pdf,.tiff,.tif,.jpg,.jpeg"
                                style={{ display: "none" }}
                                onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) handleFileSelect(file);
                                    e.target.value = "";
                                }}
                            />
                            {selectedFile ? (
                                <p><strong>{selectedFile.name}</strong> ({(selectedFile.size / 1024).toFixed(0)} KB)</p>
                            ) : (
                                <p>Drop a file here, or click to select.<br />PDF, TIFF, or JPG.</p>
                            )}
                        </div>
                    ) : (
                        <>
                            <textarea
                                className="upload-panel-textarea"
                                aria-label="Text to process"
                                placeholder="Paste your clinical text here..."
                                value={uploadTextContent}
                                onChange={(e) => setUploadTextContent(e.target.value)}
                            />
                        </>
                    )}
                </div>
            )}

            {/* Action buttons: hero (centered) or compact (inline) — above chat area */}
            <TriggerChips
                chips={chips}
                flowChips={flowChips}
                onChipClick={handleChipClick}
                disabled={isViewOnly}
                isCompact={currentContext === "documentEditor" || currentContext === "patientDetail" || currentContext === "patientsList" || chatMessages.length > 0 || isStreaming}
                docTypeDropdown={docTypeDropdown}
                onCancelConfirmation={showRecordingConfirmation ? () => session?.cancelConfirmation?.() : null}
            />

            {/* Chat: pure chat thread (hidden when collapsed, during upload, recording-only mode, or AI off) */}
            {flags.aiAssistant && !recordingOnly && !chatCollapsed && !(showUploadUI && uploadStatus === "idle") && (
                <ChatMessages
                    messages={messages}
                    isLoading={chatLoading}
                    error={chatError}
                    onRetry={chatRetry}
                    isOffline={networkStatus === "offline"}
                />
            )}

            {/* Mic selection menu — anchored to "Select Mic" chip */}
            <Menu
                anchorEl={micMenuAnchor}
                open={Boolean(micMenuAnchor)}
                onClose={() => setMicMenuAnchor(null)}
                slotProps={{
                    paper: {
                        sx: {
                            ...menuPaperSx,
                            minWidth: 200,
                        },
                    },
                }}
            >
                {(session?.audioDevices || []).map((d, idx) => {
                    const shortLabel = (d.label || `Microphone ${idx + 1}`).replace(/\s*\([0-9a-f]{4}:[0-9a-f]{4}\)\s*$/i, "");
                    const isSelected = d.deviceId === session?.selectedDeviceId;
                    return (
                        <MenuItem
                            key={d.deviceId}
                            selected={isSelected}
                            onClick={() => {
                                if (isStreaming) {
                                    session?.swapMicDuringRecording?.(d.deviceId);
                                } else {
                                    session?.selectDevice?.(d.deviceId);
                                }
                                setMicMenuAnchor(null);
                            }}
                            sx={menuItemSx}
                        >
                            {isSelected ? <Check size={14} /> : <Mic size={14} />}
                            {shortLabel}
                        </MenuItem>
                    );
                })}
            </Menu>

            {/* Input row + collapse toggle wrapper (hidden in recording-only mode or when AI is off) */}
            {!recordingOnly && flags.aiAssistant && <div className="activity-bar-input-wrapper">
                <InputRow
                    ref={inputRowRef}
                    onSend={handleSend}
                    disabled={networkStatus === "offline"}
                    dropdownState={dropdownState}
                    onFileDrop={handleFileDrop}
                    placeholder={
                        currentContext === "patientDetail" ? "Ask about this patient\u2026"
                        : currentContext === "documentEditor" ? "Ask about this document\u2026"
                        : "Search or ask anything..."
                    }
                    chatPresets={chatPresets}
                    onPresetClick={handleChatPreset}
                    aiEnabled={!!flags.aiAssistant}
                    onClearChat={chatMessages.length > 0 ? clearChat : null}
                    quickSearchPatients={quickSearchPatients}
                    quickSearchDirection={inline ? "down" : "up"}
                />
                {/* Collapse/expand chat toggle — only visible when chat has messages */}
                {hasChat && (
                    <Tooltip
                        title={chatCollapsed ? "Show chat" : "Hide chat"}
                        arrow
                        placement="top"
                    >
                        <button
                            className="activity-bar-collapse-chat-btn"
                            onClick={() => setChatCollapsed(!chatCollapsed)}
                            aria-label={chatCollapsed ? "Show chat" : "Hide chat"}
                        >
                            {chatCollapsed ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </button>
                    </Tooltip>
                )}
            </div>}

        {/* Phase 4: Quality gate dialog */}
        <QualityGateDialog
            isOpen={!!session?.showQualityGate}
            label={session?.qualityAssessment?.label}
            blocked={session?.qualityAssessment?.blocked}
            blockReason={session?.qualityAssessment?.blockReason}
            summary={session?.qualityAssessment?.summary}
            onProcessAnyway={() => session?.processAnyway?.()}
            onDiscard={() => session?.discardRecording?.()}
        />

        <CancelRecordingConfirmModal
            isOpen={!!session?.showCancelDialog}
            onCancel={() => session?.dismissCancelDialog?.()}
            onConfirm={() => session?.confirmCancel?.()}
        />

        {/* Manage Documents modal — lets users prune their document type dropdown */}
        <ManageDocumentTypesModal
            open={isManageDocsOpen}
            onClose={() => setIsManageDocsOpen(false)}
            userId={userId}
            currentSettings={dropdownState?.userSettings}
            onSaved={async () => {
                await dropdownState?.refreshUserSettings?.();
                window.dispatchEvent(new CustomEvent("documentTypesUpdated"));
            }}
        />
        </div>
    );

    // Dashboard: wrap bar in a detached card
    if (isDashboardContext) {
        return (
            <div className="activity-bar-card-wrapper">
                {barContent}
            </div>
        );
    }

    return barContent;
};

export default ActivityBar;
